import React, {useEffect, useState} from 'react';
import {Alert, Badge, Col, Row, Segmented, Select, Spin, Tag, Typography} from 'antd';
import {LeftOutlined, RightOutlined, RobotOutlined, SafetyCertificateOutlined} from '@ant-design/icons';
import TradingForm, {TradeEvent} from './components/TradingForm';
import OrderList from './components/OrderList';
import AccountDetailsCard from '../../components/AccountDetailsCard';
import RiskControlOrderList from '../../components/risk-control/RiskControlOrderList';
import {getActiveApiKeys} from '../../services/tradingService';
import {RiskMode, RiskModeMap, TradingStyle, TradingStyleMap, UserAiTradeConfig} from '../user-ai-config/types';
import {userAiConfigService} from '../user-ai-config/userAiConfigService';
import {useAuth} from '../../contexts/AuthContext';
import './TradingPage.css';

const {Title, Text} = Typography;

interface ApiKey {
    keyId: number;
    keyName: string;
    accessKey: string;
    vendor: string;
    status: string;
    isLiveTrading: boolean;
    isDefault: boolean;
    createdTime: string;
}


const OkxTradingPage: React.FC = () => {
    const {selectedApiKey, setSelectedApiKey} = useAuth();
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [userAiConfig, setUserAiConfig] = useState<UserAiTradeConfig | null>(null);
    // 交易模式: SWAP=永续合约(默认), SPOT=现货
    const [tradeMode, setTradeMode] = useState<'SPOT' | 'SWAP'>('SWAP');
    // 风控栏目显示状态 - 默认隐藏
    const [riskControlVisible, setRiskControlVisible] = useState(false);
    // 交易操作刷新令牌——下单/撤单/平仓成功后递增，驱动 OrderList 等组件执行强制刷新
    const [tradeRefreshToken, setTradeRefreshToken] = useState(0);
    const [lastTradeEvent, setLastTradeEvent] = useState<TradeEvent | null>(null);

    const handleTradeSuccess = (event: TradeEvent) => {
        setTradeRefreshToken(token => token + 1);
        setLastTradeEvent(event);
    };

    // 获取API Key列表
    const fetchApiKeys = async () => {
        try {
            const response = await getActiveApiKeys();
            const apiKeysData = response.data.data || [];
            setApiKeys(apiKeysData);

            // 情况1：如果有API Keys列表
            if (apiKeysData.length > 0) {
                // 情况1a：如果已选中项存在于列表中，保持选中状态
                const selectedExists = apiKeysData.some(key => key.keyId === selectedApiKey);
                if (selectedExists) {
                    return;
                }
                // 情况1b：优先选默认key，否则选列表第一项
                const defaultKey = apiKeysData.find(key => key.isDefault);
                setSelectedApiKey(defaultKey ? defaultKey.keyId : apiKeysData[0].keyId);
            } else {
                // 情况2：没有API Keys，清空选中状态
                setSelectedApiKey(null);
            }
        } catch (err: any) {
            // console.error('获取API Key失败:', err);
            setError('获取API Key失败');
        }
    };

    // 获取用户 AI 配置
    const fetchUserAiConfig = async () => {
        try {
            const config = await userAiConfigService.getConfig();
            setUserAiConfig(config);
        } catch (err: any) {
            // console.error('获取用户AI配置失败:', err);
        }
    };

    const getRiskModeColor = (mode: RiskMode) => {
        const colorMap: Record<RiskMode, string> = {
            [RiskMode.AUTO]: 'var(--pt-bull)',
            [RiskMode.MANUAL]: 'var(--pt-amber)',
            [RiskMode.AI]: 'var(--pt-purple)',
        };
        return colorMap[mode] || '#d9d9d9';
    };

    const getRiskModeIcon = (mode: RiskMode) => {
        const iconMap: Record<RiskMode, string> = {
            [RiskMode.AUTO]: '🤖',
            [RiskMode.MANUAL]: '👤',
            [RiskMode.AI]: '🧠',
        };
        return iconMap[mode] || '•';
    };

    const getRiskModeLabel = (mode: RiskMode) => {
        const labelMap: Record<RiskMode, string> = {
            [RiskMode.AUTO]: '自动风控',
            [RiskMode.MANUAL]: '手动风控',
            [RiskMode.AI]: 'AI风控',
        };
        return labelMap[mode] || RiskModeMap[mode] || mode;
    };

    const getTradingStyleColor = (style: TradingStyle) => {
        const colorMap: Record<TradingStyle, string> = {
            [TradingStyle.C1_CONSERVATIVE]: 'var(--pt-bull)',
            [TradingStyle.C2_CAUTIOUS]: '#a0d911',
            [TradingStyle.C3_MODERATE]: 'var(--pt-amber)',
            [TradingStyle.C4_ACTIVE]: 'var(--pt-amber)',
            [TradingStyle.C5_AGGRESSIVE]: 'var(--pt-bear)',
        };
        return colorMap[style] || '#d9d9d9';
    };

    const getTradingStyleIcon = (style: TradingStyle) => {
        const iconMap: Record<TradingStyle, string> = {
            [TradingStyle.C1_CONSERVATIVE]: '🛡️',
            [TradingStyle.C2_CAUTIOUS]: '⚖️',
            [TradingStyle.C3_MODERATE]: '📊',
            [TradingStyle.C4_ACTIVE]: '⚡',
            [TradingStyle.C5_AGGRESSIVE]: '🚀',
        };
        return iconMap[style] || '📈';
    };

    // 初始化数据加载
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                // 并行获取 API Keys 和用户 AI 配置
                await Promise.all([
                    fetchApiKeys(),
                    fetchUserAiConfig()
                ]);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    if (loading) {
        return (
            <div className="trading-page">
                <div className="loading-container">
                    <Spin size="large"/>
                    <Text style={{marginTop: 16, display: 'block'}}>加载交易数据中...</Text>
                </div>
            </div>
        );
    }

    return (
        <div className="trading-page">
            <div className="page-header">
                <Title level={2}></Title>
                <div className="header-info">
                    {userAiConfig && (
                        <>
                            <div className="risk-control-display" style={{marginRight: '16px'}}>
                                <RobotOutlined style={{marginRight: '6px', color: 'rgba(255, 255, 255, 0.65)'}}/>
                                <Text strong style={{marginRight: '6px', color: 'rgba(255, 255, 255, 0.85)'}}>
                                    风控:
                                </Text>
                                <Badge
                                    color={getRiskModeColor(userAiConfig.riskMode)}
                                    text={
                                        <span style={{color: 'rgba(255, 255, 255, 0.85)'}}>
                                            {getRiskModeIcon(userAiConfig.riskMode)} {getRiskModeLabel(userAiConfig.riskMode)}
                                        </span>
                                    }
                                />
                            </div>
                            <div className="trading-style-display" style={{marginRight: '16px'}}>
                                <span style={{marginRight: '6px', color: 'rgba(255, 255, 255, 0.65)'}}>
                                    {getTradingStyleIcon(userAiConfig.tradingStyle)}
                                </span>
                                <Text strong style={{marginRight: '6px', color: 'rgba(255, 255, 255, 0.85)'}}>
                                    交易风格:
                                </Text>
                                <Badge
                                    color={getTradingStyleColor(userAiConfig.tradingStyle)}
                                    text={
                                        <span style={{color: 'rgba(255, 255, 255, 0.85)'}}>
                                            {TradingStyleMap[userAiConfig.tradingStyle].label}
                                        </span>
                                    }
                                />
                            </div>
                        </>
                    )}
                    <div className="trade-mode-switch">
                        <Segmented
                            value={tradeMode}
                            onChange={(value) => setTradeMode(value as 'SPOT' | 'SWAP')}
                            options={[
                                {label: '永续合约', value: 'SWAP'},
                                {label: '现货', value: 'SPOT'},
                            ]}
                        />
                    </div>
                    <div className="api-key-selector">
                        <Text strong style={{marginRight: 8}}>API Key: </Text>
                        <Select
                            value={selectedApiKey}
                            onChange={setSelectedApiKey}
                            placeholder="选择API Key"
                            style={{
                                minWidth: 180,
                                maxWidth: 250
                            }}
                            size="middle"
                            showSearch={apiKeys.length > 0}
                            filterOption={(input, option) =>
                                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={apiKeys.length === 0}
                            notFoundContent={apiKeys.length === 0 ? '暂无API连接配置' : null}
                        >
                            {apiKeys.map((apiKey) => (
                                <Select.Option key={apiKey.keyId} value={apiKey.keyId}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <span>{apiKey.keyName} ({apiKey.vendor})</span>
                                        <Tag
                                            color={apiKey.isLiveTrading ? 'red' : 'green'}
                                            style={{marginLeft: 8}}
                                        >
                                            {apiKey.isLiveTrading ? '实盘' : '模拟'}
                                        </Tag>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </div>

            {error && (
                <Alert
                    message="加载失败"
                    description={error}
                    type="error"
                    style={{marginBottom: 16}}
                    closable
                    onClose={() => setError(null)}
                />
            )}

            <div className="trading-container" style={{position: 'relative'}}>
                {/* 侧边栏控制把手 - 仅在隐藏时显示 */}
                {!riskControlVisible && (
                    <div
                        onClick={() => setRiskControlVisible(true)}
                        style={{
                            position: 'absolute',
                            top: '150px', // 调整垂直位置，避开头部
                            right: '0',
                            width: '16px',
                            height: '100px',
                            background: 'var(--pt-bg-panel)',
                            borderRadius: '8px 0 0 8px', // 左侧圆角
                            border: '1px solid #424242',
                            borderRight: 'none',
                            cursor: 'pointer',
                            zIndex: 99,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '-2px 0 8px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s'
                        }}
                        title="展开风控审核"
                        className="risk-control-handle"
                    >
                        <SafetyCertificateOutlined
                            style={{color: 'var(--pt-amber)', fontSize: '12px', marginBottom: '8px'}}/>
                        <div style={{
                            writingMode: 'vertical-rl',
                            textOrientation: 'upright',
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: '10px',
                            letterSpacing: '2px',
                        }}>
                            风控
                        </div>
                        <LeftOutlined style={{fontSize: '10px', marginTop: '8px', color: 'rgba(255,255,255,0.45)'}}/>
                    </div>
                )}

                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={riskControlVisible ? 13 : 15} style={{transition: 'all 0.3s ease'}}>
                        {/* 账户详情卡片 */}
                        <div style={{marginBottom: 16}}>
                            <AccountDetailsCard
                                apiKeyId={selectedApiKey}
                                refreshInterval={5000}
                                autoRefresh={true}
                                title="账户详情"
                            />
                        </div>

                        {/* 交易表单 */}
                        <TradingForm
                            apiKeys={apiKeys}
                            selectedApiKey={selectedApiKey}
                            tradeMode={tradeMode}
                            onTradeSuccess={handleTradeSuccess}
                        />
                    </Col>
                    <Col xs={24} lg={riskControlVisible ? 7 : 9} style={{transition: 'all 0.3s ease'}}>
                        <OrderList
                            apiKeyId={selectedApiKey}
                            tradeMode={tradeMode}
                            onOrderUpdate={() => {
                                // 订单更新后的处理
                            }}
                            tradeRefreshToken={tradeRefreshToken}
                            lastTradeEvent={lastTradeEvent}
                        />
                    </Col>

                    {/* 风控列表栏目 - 仅在visible为true时渲染 */}
                    {riskControlVisible && (
                        <Col xs={24} lg={4} style={{transition: 'all 0.3s ease', position: 'relative', zIndex: 100}}>
                            <div style={{position: 'relative', height: '100%'}}>
                                {/* 关闭按钮 - 调整样式为侧边提手 */}
                                <div
                                    onClick={() => setRiskControlVisible(false)}
                                    style={{
                                        position: 'absolute',
                                        left: '-12px', // 贴在左侧外
                                        top: '150px', // 与展开前的提手位置对齐
                                        width: '12px',
                                        height: '100px',
                                        background: 'var(--pt-bg-panel)',
                                        borderRadius: '8px 0 0 8px', // 左侧圆角
                                        border: '1px solid #424242',
                                        borderRight: 'none',
                                        cursor: 'pointer',
                                        zIndex: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '-2px 0 8px rgba(0,0,0,0.2)',
                                        color: 'rgba(255, 255, 255, 0.45)'
                                    }}
                                    title="收起风控审核"
                                    className="risk-control-close-handle"
                                >
                                    <RightOutlined style={{fontSize: '10px'}}/>
                                </div>
                                <RiskControlOrderList
                                    apiKeyId={selectedApiKey}
                                    onOrderUpdate={() => {
                                        // 风控审核操作后的处理
                                    }}
                                />
                            </div>
                        </Col>
                    )}
                </Row>
            </div>
        </div>
    );
};

export default OkxTradingPage;
